import React, {useMemo, useEffect, useState} from "react";
import InfoIcon from "@mui/icons-material/Info";
import ToolTip from "@mui/material/Tooltip";
import "./RetailProcurement.scss";
import {useSelector} from "react-redux";
import {Chart as ChartJS, registerables} from "chart.js";
import {Client, isEmpty} from "weed-js";
import {InventoryRecommendation} from "../../../../../../models/lab";
import {Product, Inventory} from "../../../../../../models/dutchie.js";
import {Autocomplete, Button} from "@mui/material";
import TextField from "@mui/material/TextField";
import {Loading} from "../../../../../../components/elements/Loading";
import {DutchieUtils, MetrcUtils} from "../ProductDetail/utils";
import {KPIGrid} from "../ProductDetail/KPIGrid/KPIGrid";
import {uniqueById} from "../../../../../../utils";
import {DUTCHIE} from "../../../../../../constants";
import {CanonicalIntegrationAccount, CanonicalProduct} from "../../../../../../models/canonical";
import {MetrcAccount} from "../../../../../../models/metrc";
import {DutchieAccount} from "../../../../../../models/dutchie";


ChartJS.register(...registerables);


const ProductComparison = ({products, inventories}) => {
    const selectedIntegrationAccounts = useSelector(state => state.integrationReducer.accounts.selectedAccounts);
    const client = new Client();
    const [productIdA, setProductIdA] = useState(1310344);
    const [productIdB, setProductIdB] = useState(1200674);
    const [productA, setProductA] = useState(null);
    const [productB, setProductB] = useState(null);
    const [items, setItems] = useState([]);
    const comparisonAbortControllerRef = React.useRef(null);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [inProgress, setInProgress] = useState(0);
    const [errors, setErrors] = useState({});
    const [productAWindow, setProductAWindow] = useState({});
    const [productBWindow, setProductBWindow] = useState({});
    const selectedIntegration = useSelector(state => state.integrationReducer.selectedIntegration);
    const [nothingFound, setNothingFound] = useState({a: false, b: false});

    const inventoryByProductId = useMemo(() => {
        return inventories.reduce((acc, inv) => {
            acc[inv.product_id] = inv;
            return acc;
        }, {});
    }, [inventories]);

    const leftProductComparison = productA && nothingFound.a ? <p style={{color: "red"}}>No transactions found for {productA.name}.</p> : <KPIGrid
        activeWindow={productAWindow}
        binSizeDays={1}
        inventoryByProductId={inventoryByProductId}
        product={productA}
        setActiveWindow={setProductAWindow}
        setUserDefinedWindow={() => {}}
        transactionItems={items}
        userDefinedWindow={{start: null, end: null}}
        defaultStart={productAWindow?.previous?.start}
        defaultMiddle={productAWindow?.current?.start}
        defaultEnd={productAWindow?.current?.end}
        showBaseline={false}
        showComparison={false}
        showBaselineVSComparison={true}
        hideControls={true}
        titles={{baselineVsComparison: productA?.name || "Product A"}}
        showCharts={true}
    />

    const rightProductComparison = productB && nothingFound.b ? <p style={{color: "red"}}>No transactions found for {productB.name}</p> : <KPIGrid
        activeWindow={productBWindow}
        binSizeDays={1}
        inventoryByProductId={inventoryByProductId}
        product={productB}
        setActiveWindow={setProductBWindow}
        setUserDefinedWindow={() => {}}
        transactionItems={items}
        userDefinedWindow={{start: null, end: null}}
        defaultStart={productBWindow?.previous?.start}
        defaultMiddle={productBWindow?.current?.start}
        defaultEnd={productBWindow?.current?.end}
        showBaseline={false}
        showComparison={false}
        showBaselineVSComparison={true}
        hideControls={true}
        titles={{baselineVsComparison: productB?.name || "Product B"}}
        showCharts={true}
    />

    selectedIntegrationAccounts.map(ia => {
        if (selectedIntegration === DUTCHIE) {
            let existing = DutchieAccount.byId.get(ia.id) || new DutchieAccount(ia);
            return CanonicalIntegrationAccount.from(existing);
        }
        let existing = MetrcAccount.byId.get(ia.id) || new MetrcAccount(ia);
        return CanonicalIntegrationAccount.from(existing);
    });

    console.log("Integration accounts", selectedIntegrationAccounts);

    const percentComplete = (total, inProgress) => {
        if (total === 0) return 0;
        return (((total - inProgress) / total) * 100).toFixed(0) + "%";
    }

    const options = useMemo(() => {
        return products.map(product => {
            return {label: product.name, value: product.id}
        }).sort((a, b) => a?.label?.localeCompare(b?.label || ""));
    }, [products]);

    const productAOptions = useMemo(() => {
        return options.filter(p => p.value !== productIdB && p.label?.trim() !== "");
    }, [options, productIdA]);

    const productBOptions = useMemo(() => {
        return options.filter(p => p.value !== productIdA && p.label?.trim() !== "");
    }, [options, productIdB]);

    const productAValue = useMemo(() => {
        const product = CanonicalProduct.byId.get(productIdA);
        if (!productIdA || !product) return {label: "", value: null};
        return {label: product.name, value: product.id};
    }, [options, productIdA]);

    const productBValue = useMemo(() => {
        const product = CanonicalProduct.byId.get(productIdB);
        if (!productIdB || !product) return {label: "", value: null};
        return {label: product.name, value: product.id};
    }, [options, productIdB]);

    if (isLoading) {
        return <Loading title={"Loading transaction history..."} />;
    }
    if (isEmpty(selectedIntegrationAccounts)) {
        return <div style={{width: "100%", padding: "20px"}}>No integration accounts selected.</div>;
    }

    return <div className={"product-comparison"}>
        <h2 className={"product-comparison-title"}>
            <ToolTip title={"Choose products below to do a side-by-side comparison of their KPIs over the time range over which they were most recently active. We&apos;ll show you the trajectory of that product from the start to the end of the period shown."}><InfoIcon/> Product Comparison</ToolTip>
        </h2>
        <div className={"set-up-performance-tracking"}>
            {isLoading && <Loading title={`Loading products ${percentComplete(total, inProgress)}`}/>}
            <p>Compare &nbsp;</p>
            <Autocomplete
                key={"product-a-autocomplete"}
                className={"product-variant-dropdown"}
                filterSelectedOptions
                id={"product-a-"}
                name={"product-a"}
                role={"select-product-a-variant"}
                options={productAOptions}
                disablePortal={false}
                disabled={isEmpty(products)}
                value={productAValue}
                getOptionLabel={(option) => option.label || ""}
                isOptionEqualToValue={(option, value) => {
                    return option.value === value.value;
                }}
                onChange={(evt, option) => {
                    setProductIdA(option?.value);
                }}
                renderOption={(props, option) => {
                    return <li {...props} key={"select-product-a-option-" + option.value}>
                        {option.label}
                    </li>
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label=""
                        placeholder="Product A"
                        role={"select-product-a-input"}
                        key={"select-product-a-input"}
                    />
                )}
            />
            <p>&nbsp; with &nbsp;</p>
            <Autocomplete
                key={"product-b-autocomplete"}
                className={"product-variant-dropdown"}
                filterSelectedOptions
                id={"product-b-"}
                name={"product-b"}
                role={"select-product-b-variant"}
                options={productBOptions}
                value={productBValue}
                disablePortal={false}
                disabled={isEmpty(products)}
                getOptionLabel={(option) => option.label || ""}
                isOptionEqualToValue={(option, value) => {
                    return option.value === value.value;
                }}
                onChange={(evt, option) => {
                    setProductIdB(option?.value);
                }}
                renderOption={(props, option) => {
                    return <li {...props} key={"select-product-b-option-" + option.value}>
                        {option.label}
                    </li>
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label=""
                        placeholder="Product B"
                        role={"select-product-b-input"}
                        key={"select-product-b-input"}
                    />
                )}
            />
            <p>&nbsp;</p>
            <div>
                <Button
                    disabled={isEmpty(productIdA) || isEmpty(productIdB) || isLoading || productIdA === productIdB}
                    variant={"contained"}
                    onClick={() => {
                        setIsLoading(true);
                        const common = {
                            accountIds: selectedIntegrationAccounts.map(ia => ia.id),
                            abortControllerRef: comparisonAbortControllerRef
                        };
                        const utils = selectedIntegration === DUTCHIE ? DutchieUtils : MetrcUtils;
                        Promise.all([
                            utils.getActiveTimeRange({
                                client,
                                productId: productIdA, ...common
                            }),
                            utils.getActiveTimeRange({
                                client,
                                productId: productIdB, ...common
                            }),
                        ]).then(([productAWindowDates, productBWindowDates]) => {
                            const paw =  {
                                previous: {start: productAWindowDates[0], end: productAWindowDates[1]},
                                current: {start: productAWindowDates[1], end: productAWindowDates[2]}
                            };
                            const pbw = {
                                previous: {start: productBWindowDates[0], end: productBWindowDates[1]},
                                current: {start: productBWindowDates[1], end: productBWindowDates[2]}
                            };
                            setProductAWindow(paw);
                            setProductBWindow(pbw);
                            return Promise.all([
                                utils.getTransactionsForProductForWindow({
                                    client,
                                    productId: productIdA,
                                    activeWindow: paw,
                                    abortControllerRef: comparisonAbortControllerRef,
                                    integrationAccountIds: selectedIntegrationAccounts.map(ia => ia.id),
                                    setTotal: setTotal,
                                    setIsLoading: () => {},
                                    setInProgress: setInProgress
                                }),
                                utils.getTransactionsForProductForWindow({
                                    client,
                                    productId: productIdB,
                                    activeWindow: pbw,
                                    abortControllerRef: comparisonAbortControllerRef,
                                    integrationAccountIds: selectedIntegrationAccounts.map(ia => ia.id),
                                    setTotal: setTotal,
                                    setIsLoading: () => {},
                                    setInProgress: setInProgress
                                })
                            ]).then(([fetchedProductAData, fetchedProductBData]) => {
                                // These three will be global and the same for both over a given time range:
                                setItems(uniqueById([fetchedProductAData.items, fetchedProductBData.items].flat()));

                                setProductA(fetchedProductAData.product);
                                setProductB(fetchedProductBData.product);

                                if (isEmpty(fetchedProductAData?.items?.filter(i => i.product_id === productIdA))) {
                                    setNothingFound(prev => ({...prev, a: true}));
                                } else {
                                    setNothingFound(prev => ({...prev, a: false}));
                                }
                                if (isEmpty(fetchedProductBData?.items?.filter(i => i.product_id === productIdB))) {
                                    setNothingFound(prev => ({...prev, b: true}));
                                } else {
                                    setNothingFound(prev => ({...prev, b: false}));
                                }

                                setIsLoading(false);
                                setInProgress(0);
                            }).catch(err => {
                                console.error(err);
                                setIsLoading(false);
                                setInProgress(0);
                            });
                        })
                    }}>Go</Button>
            </div>
        </div>

        {isEmpty(errors) ? "" : <div className={"error-messages"}>
            {Object.keys(errors).map((key) => (
                <p key={key} className={"error-message"}>{errors[key]}</p>
            ))}
        </div>}

        {!productA || !productB ? <p className={"product-comparison-grid"}>Select two products to compare</p> :

            <div className={"product-comparison-grid"}>
                <div className={"product-comparison-grid-column product-a-comparison-grid"}>
                    {leftProductComparison}
                </div>
                <div className={"product-comparison-grid-column product-b-comparison-grid"}>
                    {rightProductComparison}
                </div>
            </div>}
    </div>
};


export default ProductComparison;
